import { Component, EventEmitter, inject, OnChanges, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { STORAGE_KEYS } from '../../../../../shared/constants/storage-keys';
import { ErrorCode } from '../../../../../shared/models/error-code/error-code';
import { ErrorCodeApiService } from '../../../../services/api/error-code-api.service';
import { AccessRightsService } from '../../../../services/access-rights.service';
import { Policy } from '../../../../../shared/models/info/policy';
import { errorToString } from '../../../../common/common-utils';
import { TextAreaComponent } from '../../../common/text-area/text-area.component';
import { AppInsightsService } from '../../../../services/app-insights.service';
import { SelectionOptionValue } from '../../../../../shared/models/internal/selection-option-value';
import { SelectionComponent } from '../../../common/selection/selection.component';
import { FloppyIconComponent } from '../../../icons/floppy-icon/floppy-icon.component';
import { errorCodeCodeDefaultPlaceholer, errorCodeCodeRegExValidator } from '../../../../common/error-code-validation';
import { TrackUnsavedFieldDirective } from '../../../../directives/track-unsaved-field.directive';
import { EditTechnicalRedactionReviewableBase } from '../../../base/edit-technical-redaction-reviewable/edit-technical-redaction-reviewable-base';
import { LabelWithHelp } from '../../../common/label-with-help/label-with-help';
import { NwNumberApiService } from '../../../../services/api/nw-number-api.service';
import { minSelectedValidator } from '../../../../../shared/validators/min-selected.validator';
import { NwNumber } from '../../../../../shared/models/nw-number/nw-number';
import { MultiSelectionComponent } from '../../../common/multi-selection/multi-selection.component';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';
import { HotToastService } from '@ngxpert/hot-toast';

@Component({
  selector: 'app-edit-error-code',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    TextAreaComponent,
    SelectionComponent,
    MultiSelectionComponent,
    FloppyIconComponent,
    TrackUnsavedFieldDirective,
    LabelWithHelp,
    AgentActionDirective,
  ],
  templateUrl: './edit-error-code.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './edit-error-code.component.scss',
})
export class EditErrorCodeComponent
  extends EditTechnicalRedactionReviewableBase<ErrorCode>
  implements OnInit, OnChanges
{
  @Output() public errorCodeUpdated = new EventEmitter<ErrorCode>();

  public elevatedEditDisabled = true;
  public basicEditDisabled = true;
  public editMessageDisabled = true;
  public updateTranslationFieldDisabled = true;
  public errorCodeCategories: SelectionOptionValue[] = [{ id: '-1', selected: true, title: 'Select a category' }];
  public errorCodeCodePlaceholder = errorCodeCodeDefaultPlaceholer;
  public hasCategoryUnsavedChanges = false;
  public nwNumberValues: SelectionOptionValue[] = [];
  public hasNwNumbersListUnsavedChanges = false;

  protected override readonly formGroup: FormGroup<{
    causeIntern: FormControl<string | null>;
    causeDealer: FormControl<string | null>;
    causeFarmer: FormControl<string | null>;
    remedyFarmer: FormControl<string | null>;
    remedyDealer: FormControl<string | null>;
    remedyIntern: FormControl<string | null>;
    message: FormControl<string | null>;
    machineType: FormControl<string | null>;
    category: FormControl<string | null>;
    needsReview: FormControl<boolean | null>;
    fitnessExclude: FormControl<boolean | null>;
    aggregateMessage: FormControl<boolean | null>;
    code: FormControl<string | null>;
    internalNotes: FormControl<string | null>;
    startingWithVersion: FormControl<string | null>;
    endingWithVersion: FormControl<string | null>;
    nwNumbers: FormControl;
  }>;

  private errorCodeApi = inject(ErrorCodeApiService);
  private toast = inject(HotToastService);
  private accessRightsService = inject(AccessRightsService);
  private appInsightsService = inject(AppInsightsService);
  private nwNumberApi = inject(NwNumberApiService);
  private nwNumberList?: NwNumber[];

  constructor() {
    super(STORAGE_KEYS.UNSAVED_ERROR_CODE_CHANGES);
    this.formGroup = this.formBuilder.group({
      causeIntern: new FormControl<string | null>(null),
      causeDealer: new FormControl<string | null>(null),
      causeFarmer: new FormControl<string | null>(null),
      remedyFarmer: new FormControl<string | null>(null),
      remedyDealer: new FormControl<string | null>(null),
      remedyIntern: new FormControl<string | null>(null),
      message: new FormControl<string | null>(null),
      machineType: new FormControl<string | null>(null),
      category: new FormControl<string | null>(null),
      needsReview: new FormControl<boolean | null>(null),
      fitnessExclude: new FormControl<boolean | null>(null),
      aggregateMessage: new FormControl<boolean | null>(null),
      code: new FormControl<string | null>(null, [Validators.required, errorCodeCodeRegExValidator]),
      internalNotes: new FormControl<string | null>(null),
      nwNumbers: new FormControl(null, [minSelectedValidator(1)]),
      startingWithVersion: new FormControl<string | null>(null),
      endingWithVersion: new FormControl<string | null>(null),
    });
  }

  public ngOnInit(): void {
    this.fetchErrorCodeCategories();
    this.fetchNwNumbers();

    this.init();
    this.appInsightsService.logSidebarUsage('EC_edit');
  }

  public ngOnChanges(): void {
    this.init();
    this.resetFieldTracking$.next(undefined);
    this.previousItemId = this.item?.id;
  }

  public saveErrorCode(): void {
    this.item = this.getCurrentItemFromFormValues();
    if (this.item) {
      this.errorCodeApi.updateErrorCode(this.item).subscribe({
        next: (updatedErrorCode) => {
          this.toast.success('Error Code updated successfully');
          this.item!.rowVersion = updatedErrorCode.rowVersion;

          this.errorCodeUpdated.emit(this.item);
          this.resetFieldTracking$.next(undefined);
        },
        error: (error) => {
          console.error(errorToString(error));
        },
      });
    }
  }

  public get isNotDefaultLanguage(): boolean {
    return !this.translationService.defaultLanguageSelected();
  }

  protected override getCurrentItemFromFormValues(): ErrorCode {
    const previousNwNumbers = this.item!.nwNumbers;
    const formValues = this.formGroup?.value;
    const result = {
      ...this.item,
      causeIntern: formValues?.causeIntern ?? undefined,
      causeDealer: formValues?.causeDealer ?? undefined,
      causeFarmer: formValues?.causeFarmer ?? undefined,
      remedyFarmer: formValues?.remedyFarmer ?? undefined,
      remedyDealer: formValues?.remedyDealer ?? undefined,
      remedyIntern: formValues?.remedyIntern ?? undefined,
      message: formValues?.message ?? undefined,
      machineType: formValues?.machineType ?? undefined,
      category: formValues?.category ?? undefined,
      needsReview: formValues?.needsReview ?? undefined,
      fitnessExclude: formValues?.fitnessExclude ?? undefined,
      aggregateMessage: formValues?.aggregateMessage ?? undefined,
      code: formValues?.code ?? undefined,
      internalNotes: formValues?.internalNotes ?? undefined,
      nwNumbers: this.mapNwNumberIdsToNwNumbers(formValues?.nwNumbers ?? undefined, previousNwNumbers),
      startingWithVersion: formValues?.startingWithVersion ?? undefined,
      endingWithVersion: formValues?.endingWithVersion ?? undefined,
    };
    return result;
  }

  private mapNwNumberIdsToNwNumbers(ids?: string[], fallback?: NwNumber[]): NwNumber[] {
    if (ids == undefined || this.nwNumberList == undefined) {
      return [];
    }
    const idSet = new Set(ids);
    const result = this.nwNumberList.filter((nw) => idSet.has(nw.id!));
    return result.length > 0 ? result : (fallback ?? []);
  }

  private init(): void {
    this.accessRightsService.getAccessRights$().subscribe((ar) => {
      this.elevatedEditDisabled = !ar.authorized(Policy.canUpdateElevated);
      this.basicEditDisabled = !ar.authorized(Policy.canUpdateBasic);
      this.updateTranslationFieldDisabled =
        !this.translationService.defaultLanguageSelected && !ar.authorized(Policy.canUpdateTranslations);
    });

    this.setErrorCodeDetails();
  }

  private setErrorCodeDetails(): void {
    if (this.item) {
      this.formGroup?.patchValue(this.item);
      this.patchNwNumbers(this.item);
    }

    if (this.elevatedEditDisabled) {
      this.formGroup?.get('fitnessExclude')?.disable();
      this.formGroup?.get('needsReview')?.disable();
      this.formGroup?.get('aggregateMessage')?.disable();
      this.formGroup?.get('message')?.disable();
      this.formGroup?.get('startingWithVersion')?.disable();
      this.formGroup?.get('endingWithVersion')?.disable();
      this.formGroup?.get('causeIntern')?.disable();
      this.formGroup?.get('causeDealer')?.disable();
      this.formGroup?.get('causeFarmer')?.disable();
      this.formGroup?.get('remedyFarmer')?.disable();
      this.formGroup?.get('remedyDealer')?.disable();
      this.formGroup?.get('remedyIntern')?.disable();
    } else {
      this.formGroup?.get('fitnessExclude')?.enable();
      this.formGroup?.get('needsReview')?.enable();
      this.formGroup?.get('aggregateMessage')?.enable();
      this.formGroup?.get('message')?.enable();
      this.formGroup?.get('startingWithVersion')?.enable();
      this.formGroup?.get('endingWithVersion')?.enable();
      this.formGroup?.get('causeIntern')?.enable();
      this.formGroup?.get('causeDealer')?.enable();
      this.formGroup?.get('causeFarmer')?.enable();
      this.formGroup?.get('remedyFarmer')?.enable();
      this.formGroup?.get('remedyDealer')?.enable();
      this.formGroup?.get('remedyIntern')?.enable();
    }

    if (this.basicEditDisabled) {
      this.formGroup?.get('machineType')?.disable();
      this.formGroup?.get('code')?.disable();
      this.formGroup?.get('category')?.disable();
      this.formGroup?.get('nwNumbers')?.disable();
    } else {
      this.formGroup?.get('machineType')?.enable();
      this.formGroup?.get('code')?.enable();
      this.formGroup?.get('category')?.enable();
      this.formGroup?.get('nwNumbers')?.enable();
    }
  }

  private patchNwNumbers(errorCode: ErrorCode): void {
    const nwNumberIds = errorCode.nwNumbers?.map((nw) => nw.id);
    this.formGroup.get('nwNumbers')?.setValue(nwNumberIds);
  }

  private fetchErrorCodeCategories(): void {
    this.errorCodeApi.getErrorCodeCategories().subscribe({
      next: (errorCodeCategories) => {
        this.errorCodeCategories = [
          ...this.errorCodeCategories,
          ...errorCodeCategories.map((category) => {
            return { id: category, selected: false, title: category };
          }),
        ];
      },
      error: (error) => {
        console.error(errorToString(error));
      },
    });
  }

  private fetchNwNumbers(): void {
    this.nwNumberApi.getAllNwNumbers().subscribe({
      next: (nwNumbers) => {
        this.nwNumberList = nwNumbers;
        this.nwNumberValues = [
          ...nwNumbers.map((nwNumber) => {
            return { id: nwNumber.id!, selected: false, title: nwNumber.number! };
          }),
        ];
      },
      error: (error) => {
        console.error(errorToString(error));
      },
    });
  }
}
